import { GrowthHerd, HerdCount } from "@/components/GrowthHerd";
import { CarrotIcon } from "@/components/CarrotIcon";
import { Reveal } from "@/components/Reveal";

// The visible symptoms: what a barn actually notices day to day. These are
// the leaves of the problem; the root lives further down the page.
const SYMPTOMS = [
  {
    title: "The vet date that slipped",
    body: "The reminder lived on a stall whiteboard, then a rainstorm and a busy week erased it. The horse was due three weeks ago.",
  },
  {
    title: "The invoice nobody can find",
    body: "Feed, farrier, hauling: paid from three accounts, filed in none. Tax season becomes an archaeology dig through group texts and glove boxes.",
  },
  {
    title: "The answer that starts with “ask Jess”",
    body: "Which mare got the updated shots? What did the client agree to in April? One person knows. When Jess is off, the barn waits.",
  },
];

/**
 * Section 2 of the descent: the foliage. The page tips from cream into light
 * green, and the copy stays at symptom level: what you can see from the aisle.
 */
export function Foliage() {
  return (
    <section
      id="problem"
      className="relative"
      style={{ background: "linear-gradient(to bottom, var(--cream), var(--leaf) 16%, var(--leaf))" }}
    >
      <div className="mx-auto max-w-6xl px-5 py-24 sm:px-8 sm:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
          <Reveal>
            <h2 className="text-4xl font-extrabold leading-[1.05] text-ink sm:text-5xl">
              Growth stops around <span className="text-field">100 horses</span>.
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-ink/70">
              Not for lack of skill or demand. Past a certain size, the craft that built the business gets
              buried under the work of running it. The better you are, the faster the mess arrives.
            </p>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-ink/70">
              From the aisle it looks like small stuff: farrier spreadsheets, registration papers, barn group
              texts, voice notes, sale contracts. Each one is a leaf on the same plant.
            </p>
          </Reveal>

          <Reveal className="growth-herd-reveal lg:pt-2" delay={90}>
            <figure className="growth-herd-frame overflow-hidden rounded-[1.8rem]">
              <div className="flex items-center justify-between gap-4 px-5 pb-2 pt-5 sm:px-7 sm:pt-6">
                <span className="text-[0.62rem] font-bold uppercase tracking-[0.2em] text-field">The scale problem</span>
                <HerdCount className="font-mono text-xs font-bold text-carrot-ink" />
              </div>
              <GrowthHerd className="growth-herd block w-full" />
              <figcaption className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 border-t border-ink/10 px-5 py-5 sm:px-7">
                <span>
                  <strong className="block font-display text-sm text-ink">1 horse</strong>
                  <span className="mt-0.5 block text-xs text-ink/65">memory fits in a head</span>
                </span>
                <CarrotIcon className="carrot-icon-brand h-5 w-4" />
                <span className="text-right">
                  <strong className="block font-display text-sm text-field-deep">100 horses</strong>
                  <span className="mt-0.5 block text-xs text-ink/65">memory needs a system</span>
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="mt-14 border-y border-ink/10 lg:grid lg:grid-cols-3">
          {SYMPTOMS.map((s, i) => (
            <Reveal key={s.title} delay={i * 70}>
              <article className={`h-full py-7 lg:px-7 lg:py-8 ${i ? "border-t border-ink/10 lg:border-l lg:border-t-0" : ""}`}>
                <span className="font-mono text-xs font-bold text-carrot-ink">0{i + 1}</span>
                <h3 className="mt-5 text-xl font-bold text-ink">{s.title}</h3>
                <p className="mt-2.5 leading-relaxed text-ink/65">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={240}>
          <p className="mx-auto mt-8 max-w-3xl text-center text-lg leading-relaxed text-ink/75">
            Every barn tool's fix is to make someone the record-keeper. Nobody got into horses for that job,
            so nobody feeds it, and the software dies in week one.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
